// @givmo/org-console — AUTOMATIC CONNECTION: the organization's one trusted issuer for its people's
// AI clients (Givmo MCP MCP-P17b, D-MCP-106; backend MCP-P17a).
//
// A sub-section of Connected AI, for a Global Admin only: whether the organization lets its people's
// AI clients connect without each of them approving on Givmo's consent screen, which identity source
// it trusts to vouch for them (Claude's issuer, or its identity provider's), who set it and when, the
// off switch, and the people who turned it off for themselves.
//
// ══ A GIVMO SURFACE, BY THE PLAN'S RULE (§2.4 item 1) ════════════════════════════════════════════
//
// Turning it on is an agent-never act (plan §2.5 items 4 and 6): a person does it here, after a fresh
// sign-in, bound to what Givmo rendered. So the flow is the server's at each step:
//   1. the admin pastes the issuer; "Check" asks the SERVER, which fetches the issuer's discovery
//      document through its guarded fetch and answers what it found (the kind, the host its keys come
//      from) or why it can't be used — the page never reads the typed address as the answer;
//   2. the disclosure is read beside that answer, and "Turn on" sends back the hash of exactly that
//      render, through `@givmo/step-up`'s gate (a stale sign-in opens the "Confirm it's you" sheet and
//      the same confirm is retried once);
//   3. a confirm the server would no longer render the same way is the 409 with a fresh render, which
//      replaces the old one in place with one line saying it changed.
//
// A session held to the emailed code's limits meets its cure as the AI request page's does (MCP-P6d,
// `org-first-passkey.tsx`): on a host where a passkey can work, the sheet's passkey arm, or the dialog
// gives way to the host's first-passkey offer in the section, and after "Not now" the section's button
// opens that offer again; elsewhere the sheet's own true line.
//
// ⚠ ABSENT RENDERS NOTHING. While the backend's `MCP_EMA_ENABLED` is off the read is the console's
// route miss, and the section is simply not there — no promise of a setting nobody can turn on.
//
// ⚠ STRICT GATE, ON A CAPABILITY (the build law): drawn only when the SETTLED standing read carries
// `revoke_connected_ai`, the authority the backend's three routes take. The read itself answers no
// one else, so a permissive gate would send every other admin's page a request answered 403.
//
// ⚠ "TURNED OFF FOR" IS EMPTY IN THIS CUT. The per-person off switch and the list that names who used
// it are a later backend cut (MCP-P17e); until its read exists the list states its empty answer only
// while automatic connection is on, the one state in which a person can turn it off.
//
// DATA CONTRACT: the `<prefix>-automatic-connection-*` test-id family, rooted at
// `<prefix>-automatic-connection`.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  TextField,
  testIdProp,
} from '@givmo/design-system';
import { isStepUpCancelled, useOptionalStepUpHost, useStepUpGate } from '@givmo/step-up';
import { orgConsoleKeys } from './org-access-queries';
import {
  AUTOMATIC_CONNECTION_ADD_PASSKEY_ROW,
  AUTOMATIC_CONNECTION_COPY as COPY,
  AUTOMATIC_CONNECTION_PASSKEY_ROW,
  AUTOMATIC_CONNECTION_REAUTH_ROW,
  automaticConnectionKindLabel,
  automaticConnectionProblemLine,
  automaticConnectionSource,
  automaticConnectionStepUpCopy,
} from './org-automatic-connection-copy';
import {
  automaticConnectionChangedTo,
  decodeAutomaticConnection,
  fetchAutomaticConnection,
  setAutomaticConnection,
  turnOffAutomaticConnection,
  type AutomaticConnection,
} from './org-automatic-connection-wire';
import { testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { useFirstPasskey } from './org-first-passkey';
import { formatOrgEventTime } from './org-decode';
import { confirmWithOf } from './org-request-page';
import { CAPABILITY_REVOKE_CONNECTED_AI, holdsCapability } from './standing-read';
import { useOrgAccessGate } from './use-org-console';

/** The setting's read, keyed under the package's root. */
export function automaticConnectionKey(orgRef: string) {
  return ['org-console', 'automatic-connection', orgRef] as const;
}

function retryUnlessAbsent(failureCount: number, error: Error): boolean {
  return !isOrgConsoleUnavailable(error) && failureCount < 1;
}

export interface OrgAutomaticConnectionProps {
  config: OrgConsoleConfig;
}

/**
 * The section, for a holder of the capability that ends a connected AI (a Global Admin, by the
 * backend's data) on a SETTLED standing read; nothing for anyone else or where it is absent.
 */
export function OrgAutomaticConnection({ config }: OrgAutomaticConnectionProps) {
  const gate = useOrgAccessGate(config.orgRef);
  // Turning it on needs a fresh sign-in, which only a page with a step-up host can ask for (each
  // console provides one around its Team access page). Where the host page provides none, the
  // setting is not drawn rather than drawn with a confirm that cannot complete.
  const stepUpHost = useOptionalStepUpHost();
  if (stepUpHost === null) return null;
  if (!holdsCapability(gate.self?.capabilities ?? null, CAPABILITY_REVOKE_CONNECTED_AI)) {
    return null;
  }
  return <AutomaticConnectionSection config={config} />;
}

function AutomaticConnectionSection({ config }: OrgAutomaticConnectionProps) {
  const prefix = `${testIdPrefix(config)}-automatic-connection`;
  const queryClient = useQueryClient();
  const key = automaticConnectionKey(config.orgRef);
  const setting = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) =>
      decodeAutomaticConnection(await fetchAutomaticConnection(config.orgRef, { signal })),
    retry: retryUnlessAbsent,
  });
  const [editing, setEditing] = useState(false);
  const [turningOff, setTurningOff] = useState(false);
  // The offer stands in this section, not in the dialog: its own "Confirm it's you" is the host's
  // sheet, which a modal's focus trap would hold shut.
  const firstPasskey = useFirstPasskey();

  const written = (next: AutomaticConnection | null) => {
    if (next !== null) queryClient.setQueryData(key, next);
    else void queryClient.invalidateQueries({ queryKey: key });
    // The change appends one row to the organization's Activity.
    void queryClient.invalidateQueries({ queryKey: orgConsoleKeys(config.orgRef).events });
    config.onAccessWritten?.();
  };

  // A drifted confirm's fresh render says what is stored NOW (plan §2.4 item 1): the page and the open
  // dialog read that, so neither goes on saying "Off" or "Turn on" over another admin's setting.
  const drifted = (fresh: AutomaticConnection) => {
    queryClient.setQueryData(key, { ...fresh, proposedChange: null, contentHash: null });
    void queryClient.invalidateQueries({ queryKey: orgConsoleKeys(config.orgRef).events });
  };

  if (setting.isError && isOrgConsoleUnavailable(setting.error)) return null;
  if (setting.isPending) return null;

  if (setting.isError || setting.data === null) {
    return (
      <section className={SECTION} {...testIdProp(prefix)}>
        <h3 className={HEADING}>{COPY.title}</h3>
        <div className="flex flex-col items-start gap-12">
          <ErrorMessage testId={`${prefix}-error`}>{COPY.loadFailed}</ErrorMessage>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void setting.refetch()}
            testId={`${prefix}-retry`}
          >
            {COPY.tryAgain}
          </Button>
        </div>
      </section>
    );
  }

  const current = setting.data;
  const stored = current.trustedIssuer;

  return (
    <section className={SECTION} {...testIdProp(prefix)} data-state={current.on ? 'on' : 'off'}>
      <div className="flex items-start justify-between gap-16">
        <div className="min-w-0">
          <h3 className={HEADING}>{COPY.title}</h3>
          <p className={`mt-4 ${LINE}`} {...testIdProp(`${prefix}-status`)}>
            {current.on && stored !== null
              ? COPY.onLine(automaticConnectionSource(stored.kind))
              : COPY.offLine}
          </p>
          {current.on && stored !== null && (
            <>
              <p
                className="mt-8 truncate font-mono text-s12 tracking-givmo text-text-secondary"
                title={stored.issuer}
                {...testIdProp(`${prefix}-issuer`)}
              >
                {stored.issuer}
              </p>
              <p className={`mt-2 ${SMALL}`} {...testIdProp(`${prefix}-set-by`)}>
                {COPY.setBy(stored.setByName, formatOrgEventTime(stored.setAt))}
              </p>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-8">
          {firstPasskey.waiting ? (
            // After "Not now" the dialog could only meet the same refusal, so this opens the offer
            // again, here. It asks the server nothing and changes nothing.
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={firstPasskey.reopen}
              testId={`${prefix}-set-up-passkey`}
            >
              {COPY.setUpPasskey}
            </Button>
          ) : (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              disabled={firstPasskey.shut}
              onClick={() => {
                firstPasskey.reset();
                setEditing(true);
              }}
              testId={current.on ? `${prefix}-change` : `${prefix}-set-up`}
            >
              {current.on ? COPY.change : COPY.setUp}
            </Button>
          )}
          {current.on && (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => setTurningOff(true)}
              testId={`${prefix}-turn-off`}
            >
              {COPY.turnOff}
            </Button>
          )}
        </div>
      </div>

      {firstPasskey.shown && (
        <p className={LINE} {...testIdProp(`${prefix}-needs-passkey`)}>
          {firstPasskey.cantBeMade
            ? COPY.passkeyCantBeMade(current.on)
            : firstPasskey.offer !== undefined
              ? COPY.setUpPasskeyThen(current.on)
              : COPY.needsPasskey(current.on)}
        </p>
      )}
      {firstPasskey.offer}

      {current.on && (
        <div {...testIdProp(`${prefix}-turned-off-for`)}>
          <h4 className="font-sans text-s13 font-w600 tracking-givmo text-text-strong">
            {COPY.turnedOffForHeading}
          </h4>
          <p className={`mt-2 ${SMALL}`} {...testIdProp(`${prefix}-turned-off-for-empty`)}>
            {COPY.turnedOffForEmpty}
          </p>
        </div>
      )}

      {editing && (
        <SetUpDialog
          config={config}
          prefix={prefix}
          current={current}
          passkeysWorkHere={firstPasskey.worksHere}
          onWantOfPasskey={(error) => {
            if (!firstPasskey.caught(error)) return false;
            setEditing(false);
            return true;
          }}
          onClose={() => setEditing(false)}
          onDrifted={drifted}
          onWritten={(next) => {
            written(next);
            setEditing(false);
          }}
        />
      )}
      {turningOff && (
        <TurnOffDialog
          config={config}
          prefix={prefix}
          onClose={() => setTurningOff(false)}
          onWritten={(next) => {
            written(next);
            setTurningOff(false);
          }}
        />
      )}
    </section>
  );
}

interface DialogProps {
  config: OrgConsoleConfig;
  prefix: string;
  onClose: () => void;
  onWritten: (next: AutomaticConnection | null) => void;
}

function SetUpDialog({
  config,
  prefix,
  current,
  passkeysWorkHere,
  onWantOfPasskey,
  onDrifted,
  onClose,
  onWritten,
}: DialogProps & {
  current: AutomaticConnection;
  passkeysWorkHere: boolean;
  /** `true` when only a passkey could answer and the account holds none: the section takes it. */
  onWantOfPasskey: (error: unknown) => boolean;
  /** A confirm the server refused as drifted: its fresh render, for the section to hold. */
  onDrifted: (fresh: AutomaticConnection) => void;
}) {
  const id = `${prefix}-dialog`;
  const [issuer, setIssuer] = useState(current.trustedIssuer?.issuer ?? '');
  const [usesTenantId, setUsesTenantId] = useState(current.trustedIssuer?.usesTenantId ?? false);
  // What the SERVER found for the address as it stood when "Check" ran. Editing the address or the
  // tenant box clears it, so a confirm is only ever of the render on the screen.
  const [checked, setChecked] = useState<AutomaticConnection | null>(null);
  // Claude's optional Tenant ID field: drawn once the SERVER says this issuer is Claude's or needs a
  // tenant to tell it from another organization's, and from then on, so an identity provider's admin
  // never meets it and ticking its box never takes it away.
  const [tenantShown, setTenantShown] = useState(
    current.trustedIssuer?.kind === 'claude' || current.trustedIssuer?.usesTenantId === true,
  );
  const [changed, setChanged] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  // The page's host, handed to the gate DIRECTLY, so the gate keeps a prompt of its own and draws its
  // sheet inside this dialog (`gate.dialog`, below): a provider-drawn sheet would open outside this
  // modal, beside its focus trap rather than within it.
  const stepUpHost = useOptionalStepUpHost();
  const gate = useStepUpGate({
    ...(stepUpHost === null ? {} : { host: stepUpHost }),
    copy: automaticConnectionStepUpCopy(passkeysWorkHere, current.on),
    testIdPrefix: `${id}-step-up`,
    // A session held to the emailed code's limits is refused with its cure named
    // (`context.confirm_with`), each opening its own row (the request page's W5 rows).
    refusalCodes: {
      passkey_setup_required: (error) =>
        confirmWithOf(error) === 'add_passkey'
          ? {
              row: AUTOMATIC_CONNECTION_ADD_PASSKEY_ROW,
              accepts: passkeysWorkHere ? ['passkey'] : [],
            }
          : confirmWithOf(error) === 'reauth'
            ? { row: AUTOMATIC_CONNECTION_REAUTH_ROW }
            : undefined,
      passkey_confirmation_required: (error) =>
        confirmWithOf(error) === 'passkey' ? { row: AUTOMATIC_CONNECTION_PASSKEY_ROW } : undefined,
    },
  });

  const check = useMutation({
    mutationFn: async (uses: boolean) =>
      decodeAutomaticConnection(
        await fetchAutomaticConnection(config.orgRef, {
          issuer: issuer.trim(),
          usesTenantId: uses,
        }),
      ),
    onMutate: () => {
      setChanged(false);
      setSaveFailed(false);
    },
    onSuccess: (render) => {
      setChecked(render);
      const found = render?.proposedChange ?? null;
      if (found?.kind === 'claude' || found?.problem === 'issuer_held_elsewhere') {
        setTenantShown(true);
      }
    },
    // Nothing checked is nothing to confirm.
    onError: () => setChecked(null),
  });

  const confirm = useMutation({
    mutationFn: async () => {
      const proposal = checked?.proposedChange ?? null;
      const contentHash = checked?.contentHash ?? null;
      if (proposal === null || contentHash === null) throw new Error('nothing to confirm');
      return decodeAutomaticConnection(
        await gate.run(() =>
          setAutomaticConnection(config.orgRef, {
            issuer: proposal.issuer,
            uses_tenant_id: proposal.usesTenantId,
            content_hash: contentHash,
          }),
        ),
      );
    },
    onMutate: () => {
      setChanged(false);
      setSaveFailed(false);
    },
    onSuccess: (next) => onWritten(next),
    onError: (error) => {
      if (isStepUpCancelled(error)) return;
      if (onWantOfPasskey(error)) return;
      const fresh = automaticConnectionChangedTo(error);
      if (fresh !== null) {
        setChecked(fresh);
        setChanged(true);
        onDrifted(fresh);
        return;
      }
      setSaveFailed(true);
    },
  });

  const edit = (next: () => void) => {
    next();
    setChecked(null);
    setChanged(false);
    setSaveFailed(false);
  };

  // Whether this issuer's assertions name the organization is part of what the server checks, so a
  // tick re-checks at once, with the value just ticked. The old render stays on screen meanwhile, its
  // confirm off (`busy`), and goes if the re-check fails.
  const tickTenant = (value: boolean) => {
    setUsesTenantId(value);
    if (issuer.trim().length > 0) check.mutate(value);
    else setChecked(null);
  };

  const proposal = checked?.proposedChange ?? null;
  const confirmable =
    proposal !== null && proposal.problem === null && checked?.contentHash != null;
  const busy = check.isPending || confirm.isPending;
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(current.organizationId);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && !busy && onClose()}>
      <Dialog.Content
        aria-label={current.on ? COPY.changeDialogTitle : COPY.dialogTitle}
        // One sheet at a time: this dialog rests while "Confirm it's you" is up over it.
        className={`flex flex-col gap-16 ${gate.prompting ? 'invisible' : ''}`}
        testId={id}
        width={480}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {current.on ? COPY.changeDialogTitle : COPY.dialogTitle}
        </Dialog.Title>
        <p className={BODY}>{COPY.dialogIntro}</p>

        <TextField
          label={COPY.issuerLabel}
          placeholder={COPY.issuerPlaceholder}
          value={issuer}
          onChange={(event) => edit(() => setIssuer(event.target.value))}
          disabled={busy}
          autoComplete="off"
          spellCheck={false}
          testId={`${id}-issuer`}
        />

        {proposal !== null && proposal.problem === null && (
          <p
            className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
            {...testIdProp(`${id}-kind`)}
          >
            {automaticConnectionKindLabel(proposal.kind)}
            {proposal.keysHost !== null && (
              <span className="font-w400 text-text-tertiary">
                {` · ${COPY.keysFrom(proposal.keysHost)}`}
              </span>
            )}
          </p>
        )}

        {tenantShown && (
          <div className="flex flex-col gap-8" {...testIdProp(`${id}-tenant`)}>
            <p className="font-sans text-s12 font-w600 tracking-givmo text-black">
              {COPY.organizationIdLabel}
            </p>
            <div className="flex items-center gap-8">
              <code
                className="min-w-0 flex-1 truncate rounded-md bg-surface-sunken px-12 py-8 font-mono text-s13 text-text-secondary"
                {...testIdProp(`${id}-organization-id`)}
              >
                {current.organizationId}
              </code>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void copyId()}
                testId={`${id}-copy-id`}
              >
                {copied ? COPY.copied : COPY.copy}
              </Button>
            </div>
            <label className="flex items-center gap-8">
              <Checkbox
                appearance="saas"
                checked={usesTenantId}
                onCheckedChange={tickTenant}
                disabled={busy}
                testId={`${id}-uses-tenant-id`}
              />
              <span className="font-sans text-s13 tracking-givmo text-text-secondary">
                {COPY.usesTenantId}
              </span>
            </label>
          </div>
        )}

        {proposal !== null && proposal.problem !== null && (
          <ErrorMessage testId={`${id}-problem`}>
            {automaticConnectionProblemLine(proposal.problem)}
          </ErrorMessage>
        )}

        {proposal !== null && proposal.problem === null && (
          <div {...testIdProp(`${id}-disclosure`)}>
            <p className={BODY}>{COPY.disclosureLead}</p>
            <ul className="mt-4 flex list-disc flex-col gap-4 pl-20">
              {COPY.disclosure(automaticConnectionSource(proposal.kind)).map((line) => (
                <li key={line} className={BODY}>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        )}

        {changed && (
          <div className="flex flex-col gap-4">
            <p className={BODY} {...testIdProp(`${id}-changed`)}>
              {COPY.changed}
            </p>
            <p className={`${BODY} break-all`} {...testIdProp(`${id}-changed-stored`)}>
              {current.trustedIssuer !== null
                ? COPY.changedStored(current.trustedIssuer.issuer, current.trustedIssuer.setByName)
                : COPY.changedStoredOff}
            </p>
          </div>
        )}
        {check.isError && (
          <ErrorMessage testId={`${id}-check-error`}>{COPY.checkFailed}</ErrorMessage>
        )}
        {saveFailed && <ErrorMessage testId={`${id}-save-error`}>{COPY.saveFailed}</ErrorMessage>}

        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={busy}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${id}-cancel`)}
          >
            {COPY.cancel}
          </Dialog.Close>
          {confirmable ? (
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={confirm.isPending && !gate.prompting}
              disabled={busy}
              onClick={() => confirm.mutate()}
              testId={`${id}-confirm`}
            >
              {current.on ? COPY.save : COPY.turnOn}
            </Button>
          ) : (
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={check.isPending}
              disabled={busy || issuer.trim().length === 0}
              onClick={() => check.mutate(usesTenantId)}
              testId={`${id}-check`}
            >
              {check.isPending ? COPY.checking : COPY.check}
            </Button>
          )}
        </div>
        {gate.dialog}
      </Dialog.Content>
    </Dialog.Root>
  );
}

function TurnOffDialog({ config, prefix, onClose, onWritten }: DialogProps) {
  const id = `${prefix}-turn-off-dialog`;
  const turnOff = useMutation({
    mutationFn: async () =>
      decodeAutomaticConnection(await turnOffAutomaticConnection(config.orgRef)),
    onSuccess: (next) => onWritten(next),
  });
  return (
    <Dialog.Root open onOpenChange={(open) => !open && !turnOff.isPending && onClose()}>
      <Dialog.Content aria-label={COPY.turnOffTitle} className="flex flex-col gap-16" testId={id}>
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {COPY.turnOffTitle}
        </Dialog.Title>
        <p className={BODY}>{COPY.turnOffBody}</p>
        {turnOff.isError && (
          <ErrorMessage testId={`${id}-error`}>{COPY.turnOffFailed}</ErrorMessage>
        )}
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={turnOff.isPending}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${id}-cancel`)}
          >
            {COPY.cancel}
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={turnOff.isPending}
            onClick={() => turnOff.mutate()}
            testId={`${id}-confirm`}
          >
            {COPY.turnOff}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

const SECTION = 'flex flex-col gap-12 border-t border-border-subtle pt-16';
const HEADING = 'font-sans text-s14 font-w700 tracking-givmo text-text-strong';
const LINE = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const SMALL = 'font-sans text-s12 font-w400 tracking-givmo text-text-tertiary';
const BODY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
